import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Clock, FileText, MoreHorizontal, Plus } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MemberByline } from '../components/wiki/MemberByline';
import AppLayout from '../components/work/AppLayout';
import ListFilterBar from '../components/work/ListFilterBar';
import { BrandEmpty, ListEmpty, LoadingList, PageTitle, WorkPageError } from '../components/work/page-states';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { useOverlays } from '../components/work/overlay-context';
import { useMembers } from '../components/wiki/use-member-names';
import { apiErrorMessage, apiFetch, v1Path } from '../lib/api';
import { projectInitial } from '../lib/assigned-queue';
import { compareText, matchesQuery } from '../lib/directory-filter';
import { formatTimestampDateTime } from '../lib/dates';
import { htmlToPlainText } from '../lib/rich-text';
import { isWikiPageSlim, listWikiSpaces, wikiRoute, type WikiPageSlim, type WikiSpaceDto, type WikiSpaceRef } from '../lib/wiki';
import { isProjectDto, parseItems, type ProjectDto } from '../lib/work-types';
import { cn } from '../lib/utils';

const WIKI_CHIPS = ['space'] as const;
const WIKI_DEFAULT_ORDER = 'name';
const WIKI_SORT_OPTIONS = [
  { value: 'name', label: 'Name A-Z' },
  { value: '-updated_at', label: 'Recently updated' },
  { value: '-pages', label: 'Most pages' },
] as const;
const FEATURED_SPACE_COUNT = 4;
const FEATURED_PAGE_COUNT = 8;
const AVATAR_TONES = [
  'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  'bg-emerald-100 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-200',
  'bg-violet-100 text-violet-900 dark:bg-violet-500/15 dark:text-violet-200',
  'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  'bg-rose-100 text-rose-900 dark:bg-rose-500/15 dark:text-rose-200',
] as const;

type Change = WikiPageSlim & { space: { kind: 'org' | 'project'; id: string | null; identifier: string } };

function isChange(value: unknown): value is Change {
  if (!isWikiPageSlim(value)) return false;
  const space = (value as Record<string, unknown>).space;
  return (
    Boolean(space) &&
    typeof space === 'object' &&
    ((space as Record<string, unknown>).kind === 'org' || (space as Record<string, unknown>).kind === 'project') &&
    typeof (space as Record<string, unknown>).identifier === 'string'
  );
}

function spaceRef(dto: { kind: 'org' | 'project'; id: string | null }): WikiSpaceRef {
  return { kind: dto.kind, projectId: dto.kind === 'project' ? dto.id : null };
}

function spaceKey(dto: { kind: string; id: string | null }): string {
  return `${dto.kind}:${dto.id ?? ''}`;
}

function spaceAvatarTone(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index)) % AVATAR_TONES.length;
  }
  return AVATAR_TONES[hash] ?? AVATAR_TONES[0];
}

function spaceDescription(row: WikiSpaceDto, projects: ProjectDto[]): string {
  if (row.kind === 'org') return 'Shared with everyone in the organization.';
  const project = projects.find((item) => item.id === row.id);
  const text = htmlToPlainText(project?.description ?? '').trim();
  return text.length > 0 ? text : 'Stream wiki.';
}

/** Screen 1b: spaces as cards plus recently updated across spaces. */
export default function Wiki() {
  const members = useMembers();
  const { toast } = useOverlays();
  const navigate = useNavigate();
  const filters = useDirectoryQuery(WIKI_CHIPS, WIKI_DEFAULT_ORDER);
  const [spaces, setSpaces] = useState<WikiSpaceDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [changes, setChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllSpaces, setShowAllSpaces] = useState(false);
  const [showAllPages, setShowAllPages] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listWikiSpaces(),
      apiFetch<unknown>(v1Path('/wiki/changes')),
      apiFetch<unknown>(v1Path('/projects?limit=100&offset=0'))
        .then((rows) => parseItems(rows, isProjectDto, 'projects'))
        .catch(() => [] as ProjectDto[]),
    ])
      .then(([spaceRows, changeRows, projectRows]) => {
        if (cancelled) return;
        setSpaces(spaceRows);
        setChanges(parseItems(changeRows, isChange, 'wiki changes').filter((row) => row.deleted_at === null));
        setProjects(projectRows);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(apiErrorMessage(err, 'Failed to load wiki'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const spaceNames = useMemo(() => new Map(spaces.map((row) => [spaceKey(row), row.name])), [spaces]);
  const selectedSpace = filters.selected.space[0] ?? null;

  const visibleSpaces = useMemo(() => {
    const rows = spaces.filter((row) => {
      if (selectedSpace && spaceKey(row) !== selectedSpace) return false;
      return matchesQuery(filters.query, [row.name, row.identifier, spaceDescription(row, projects)]);
    });
    const orderBy = filters.orderBy ?? WIKI_DEFAULT_ORDER;
    return rows.slice().sort((left, right) => {
      if (orderBy === '-updated_at') {
        return (right.updated_at ?? '').localeCompare(left.updated_at ?? '');
      }
      if (orderBy === '-pages') return right.page_count - left.page_count;
      if (left.kind !== right.kind) return left.kind === 'org' ? -1 : 1;
      return compareText(left.name, right.name);
    });
  }, [filters.orderBy, filters.query, projects, selectedSpace, spaces]);

  const featuredSpaces = showAllSpaces ? visibleSpaces : visibleSpaces.slice(0, FEATURED_SPACE_COUNT);

  const recent = useMemo(() => {
    const rows = changes.filter((row) => {
      const name = spaceNames.get(spaceKey(row.space)) ?? row.space.identifier;
      if (selectedSpace && spaceKey(row.space) !== selectedSpace) return false;
      return matchesQuery(filters.query, [row.title, row.path, name]);
    });
    return rows.slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }, [changes, filters.query, selectedSpace, spaceNames]);

  const visibleRecent = showAllPages ? recent : recent.slice(0, FEATURED_PAGE_COUNT);
  const createHref = selectedSpace
    ? `${wikiRoute(spaceRef(parseSpaceKey(selectedSpace)))}?create=1`
    : `${wikiRoute({ kind: 'org', projectId: null })}?create=1`;

  async function copyPageLink(row: Change) {
    const path = wikiRoute(spaceRef(row.space), row.path);
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      toast('Copied link');
    } catch {
      toast('Clipboard is not available');
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-1 flex-col gap-8">
        <PageTitle
          title="Wiki"
          count={loading || error ? undefined : visibleSpaces.length}
          action={
            <Button type="button" size="sm" onClick={() => navigate(createHref)}>
              <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
              Create page
            </Button>
          }
        />
        <ListFilterBar
          query={filters.query}
          onQueryChange={(query) => filters.apply({ query })}
          searchPlaceholder="Search spaces, pages or content..."
          searchAriaLabel="Search wiki"
          searchClassName="sm:max-w-md"
          afterSearch={
            <Select
              value={selectedSpace ?? 'all'}
              onValueChange={(value) => filters.apply({ selected: { space: value === 'all' ? [] : [value] } })}
            >
              <SelectTrigger aria-label="Space" className="h-8 w-auto min-w-[9rem] rounded-[10px] bg-background px-2.5 shadow-none">
                <SelectValue placeholder="All spaces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All spaces</SelectItem>
                {spaces.map((row) => (
                  <SelectItem key={spaceKey(row)} value={spaceKey(row)}>
                    {row.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
          sort={{
            value: filters.orderBy ?? WIKI_DEFAULT_ORDER,
            options: [...WIKI_SORT_OPTIONS],
            onChange: (value) => filters.apply({ orderBy: value }),
          }}
          onClear={() => {
            setShowAllSpaces(false);
            setShowAllPages(false);
            filters.clear();
          }}
        />
        <WorkPageError error={error} />
        {error ? null : loading ? (
          <LoadingList rows={6} />
        ) : spaces.length === 0 ? (
          <BrandEmpty title="No spaces yet." />
        ) : visibleSpaces.length === 0 && recent.length === 0 ? (
          <ListEmpty
            title="No spaces match"
            description="Clear filters if you expected matches."
            action={
              <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <>
            {visibleSpaces.length > 0 ? (
              <section aria-labelledby="wiki-spaces">
                <SectionHeading
                  id="wiki-spaces"
                  title="Spaces"
                  action={
                    visibleSpaces.length > FEATURED_SPACE_COUNT ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        onClick={() => setShowAllSpaces((open) => !open)}
                      >
                        {showAllSpaces ? 'Show fewer spaces' : 'View all spaces'}
                        {showAllSpaces ? null : <ArrowRight className="h-3.5 w-3.5" aria-hidden />}
                      </button>
                    ) : null
                  }
                />
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {featuredSpaces.map((row) => (
                    <SpaceCard key={spaceKey(row)} row={row} />
                  ))}
                </div>
              </section>
            ) : null}
            <section aria-labelledby="wiki-recent-all">
              <SectionHeading
                id="wiki-recent-all"
                title="Recently updated"
                action={
                  recent.length > FEATURED_PAGE_COUNT ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      onClick={() => setShowAllPages((open) => !open)}
                    >
                      {showAllPages ? 'Show fewer pages' : 'View all pages'}
                      {showAllPages ? null : <ArrowRight className="h-3.5 w-3.5" aria-hidden />}
                    </button>
                  ) : null
                }
              />
              {recent.length === 0 ? (
                changes.length === 0 ? (
                  <BrandEmpty title="No pages yet." />
                ) : (
                  <ListEmpty title="No pages match." />
                )
              ) : (
                <Table className="mt-4 table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-0 w-[32%]">Title</TableHead>
                      <TableHead className="w-48">Space</TableHead>
                      <TableHead className="w-52">Updated by</TableHead>
                      <TableHead className="w-48">Updated</TableHead>
                      <TableHead className="w-10">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleRecent.map((row) => {
                      const spaceName = spaceNames.get(spaceKey(row.space)) ?? row.space.identifier;
                      const href = wikiRoute(spaceRef(row.space), row.path);
                      return (
                        <TableRow key={row.id}>
                          <TableCell className="min-w-0">
                            <Link to={href} className="inline-flex min-w-0 items-center gap-2 font-medium hover:underline">
                              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                              <span className="truncate">{row.title}</span>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
                              {row.space.kind === 'org' ? (
                                <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              ) : (
                                <span
                                  aria-hidden
                                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-muted text-[9px] font-semibold"
                                >
                                  {projectInitial(spaceName, row.space.identifier)}
                                </span>
                              )}
                              <span className="truncate">{spaceName}</span>
                            </span>
                          </TableCell>
                          <TableCell>
                            <MemberByline
                              members={members}
                              userId={row.updated_by_id}
                              className="text-muted-foreground"
                            />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatTimestampDateTime(row.updated_at) ?? row.updated_at}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label={`Actions for ${row.title}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => navigate(href)}>Open</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => void copyPageLink(row)}>Copy link</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </section>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function parseSpaceKey(value: string): { kind: 'org' | 'project'; id: string | null } {
  if (value === 'org:') return { kind: 'org', id: null };
  if (value.startsWith('project:')) {
    const id = value.slice('project:'.length);
    return { kind: 'project', id: id.length > 0 ? id : null };
  }
  return { kind: 'org', id: null };
}

function SectionHeading({
  id,
  title,
  action,
}: {
  id: string;
  title: string;
  action: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <h2 id={id} className="text-base font-semibold tracking-tight">
        {title}
      </h2>
      {action}
    </div>
  );
}

function SpaceCard({ row }: { row: WikiSpaceDto }) {
  const href = wikiRoute(spaceRef(row));
  const updated = row.updated_at ? formatTimestampDateTime(row.updated_at) ?? row.updated_at : '—';
  return (
    <Link
      to={href}
      className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left shadow-none transition-colors hover:bg-accent/40"
    >
      {row.kind === 'org' ? (
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
        >
          <Building2 className="h-3.5 w-3.5" />
        </span>
      ) : (
        <span
          aria-hidden
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold',
            spaceAvatarTone(row.identifier),
          )}
        >
          {projectInitial(row.name, row.identifier)}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium">{row.name}</span>
          {row.kind === 'org' ? (
            <Badge variant="secondary" className="h-5 shrink-0 px-1.5 font-normal">
              Company
            </Badge>
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <FileText className="h-3 w-3" aria-hidden />
            {row.page_count} page{row.page_count === 1 ? '' : 's'}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" aria-hidden />
            {updated}
          </span>
        </div>
      </div>
    </Link>
  );
}
